import { useThreediModelQuery } from "@/queries/query-hcc-models";
import { useHccSchematisationQuery } from "@/queries/query-hcc-schematisation";
import {
  useHccLatestRevisionQuery,
  useHccRevisionQuery,
} from "@/queries/query-hcc-schematisation-revision";

import {
  SchematisationDetailsContextValue,
  SchematisationRevisionWithId,
} from "../schematisation-details-context";

/**
 * Loads the schematisation, the selected revision (or the latest revision when none is selected)
 * and the 3Di model of that revision. The revision describes the schematisation version and its
 * validation state; the 3Di model is the generated model associated with that revision and may
 * not exist yet.
 *
 * `schematisation` and `revision` are undefined while loading or when they could not be loaded.
 * Only meant to be called by the context provider.
 */
export function useSchematisationDetails(
  schematisationId: number,
  selectedRevisionId: number | undefined
): {
  isLoading: boolean;
  schematisation: SchematisationDetailsContextValue["schematisation"] | undefined;
  revision: SchematisationDetailsContextValue["revision"] | undefined;
  threediModel: SchematisationDetailsContextValue["threediModel"];
} {
  const schematisationQuery = useHccSchematisationQuery(schematisationId);

  const latestRevisionQuery = useHccLatestRevisionQuery(
    selectedRevisionId === undefined ? schematisationId : undefined
  );
  const selectedRevisionQuery = useHccRevisionQuery(schematisationId, selectedRevisionId);
  const revisionQuery =
    selectedRevisionId === undefined ? latestRevisionQuery : selectedRevisionQuery;
  // The HCC API types mark the revision id as optional, but a loaded revision always has one.
  const revision = revisionQuery.data as SchematisationRevisionWithId | undefined;

  const threediModelQuery = useThreediModelQuery({ schematisationId, revisionId: revision?.id });

  return {
    isLoading:
      schematisationQuery.isLoading || revisionQuery.isLoading || threediModelQuery.isLoading,
    schematisation: schematisationQuery.data,
    revision,
    threediModel: threediModelQuery.data ?? null,
  };
}
